import type {
	AdminAuditEntry,
	AdminAuditPageQuery,
	AdminBenefitHistoryQuery,
	AdminBenefitOperation,
	AdminBenefitResult,
	AdminBenefits,
	AdminBroadcast,
	AdminBroadcastsQuery,
	AdminBroadcastAudience,
	AdminBroadcastCreate,
	AdminOverview,
	AdminCallSummary,
	AdminCallsQuery,
	AdminChannelsQuery,
	AdminPage,
	AdminPayment,
	AdminPaymentsQuery,
	AdminMfaSetup,
	AdminPlatformSettings,
	AdminPaymentSettings,
	AdminPaymentSettingsInput,
	AdminPlatformSettingsInput,
	AdminRange,
	AdminReport,
	AdminPublicChannel,
	AdminSecurityEvent,
	AdminSecurityQuery,
	AdminSupportTicket,
	AdminTicketsQuery,
	AdminSystemSnapshot,
	AdminSupportTicketUpdate,
	AdminUser,
	AdminUserDetail,
	AdminUserProfileInput,
	AdminUserQuery,
	AdminWorkspace,
	AdminReportUpdate,
	AdminRoleInput,
	AdminPremiumAdjustInput,
	AdminStarsAdjustInput,
	AdminStarsBulkOutcome
} from '@andresot/protocol/admin';
import type { ReportCreateInput } from '@andresot/protocol/admin';
import { authorizedFetch, request } from './client.js';

export async function fetchOverview(): Promise<AdminOverview> {
	return (await request<{ stats: AdminOverview }>('/api/admin/overview')).stats;
}

export async function fetchWorkspace(range: AdminRange): Promise<AdminWorkspace> {
	return (
		await request<{ workspace: AdminWorkspace }>(
			`/api/admin/workspace?range=${range}&summaryOnly=true`
		)
	).workspace;
}

function pageParams(query: {
	q?: string;
	queue?: AdminTicketsQuery['queue'];
	status?: string;
	cursor?: string;
	limit?: number;
}): URLSearchParams {
	const params = new URLSearchParams();
	if (query.q) params.set('q', query.q);
	if (query.queue) params.set('queue', query.queue);
	if (query.status) params.set('status', query.status);
	if (query.cursor) params.set('cursor', query.cursor);
	params.set('limit', String(query.limit ?? 25));
	return params;
}

async function fetchPage<T>(
	path: string,
	query: Parameters<typeof pageParams>[0]
): Promise<AdminPage<T>> {
	return (await request<{ page: AdminPage<T> }>(`${path}?${pageParams(query)}`)).page;
}

export const fetchPaymentsPage = (query: Partial<AdminPaymentsQuery> = {}) =>
	fetchPage<AdminPayment>('/api/admin/payments', query);

export const fetchChannelsPage = (query: Partial<AdminChannelsQuery> = {}) =>
	fetchPage<AdminPublicChannel>('/api/admin/channels', query);

export const fetchCallsPage = (query: Partial<AdminCallsQuery> = {}) =>
	fetchPage<AdminCallSummary>('/api/admin/calls', query);

export const fetchTicketsPage = (query: Partial<AdminTicketsQuery> = {}) =>
	fetchPage<AdminSupportTicket>('/api/admin/support', query);

export const fetchBroadcastsPage = (query: Partial<AdminBroadcastsQuery> = {}) =>
	fetchPage<AdminBroadcast>('/api/admin/broadcasts', query);

export const fetchSecurityPage = (query: Partial<AdminSecurityQuery> = {}) =>
	fetchPage<AdminSecurityEvent>('/api/admin/security', query);

export async function fetchAuditPage(
	query: Partial<AdminAuditPageQuery> = {}
): Promise<{ page: AdminPage<AdminAuditEntry>; integrity: boolean }> {
	return request(`/api/admin/audit/page?${pageParams(query)}`);
}

/** Лёгкий снимок двух узлов: раздел «Серверы» обновляет его отдельно от рабочего стола. */
export async function fetchSystemResources(): Promise<AdminSystemSnapshot> {
	const snapshot = await request<AdminSystemSnapshot>('/api/admin/system/resources');
	return { ...snapshot, servers: snapshot.servers ?? [] };
}

export interface AdminUserPage {
	users: AdminUser[];
	total: number;
	nextCursor?: string;
}

export async function fetchUsers(
	query: Partial<AdminUserQuery> | string = {},
	cursor?: string,
	limit?: number
): Promise<AdminUserPage> {
	const normalized: Partial<AdminUserQuery> =
		typeof query === 'string' ? { q: query, cursor, limit } : query;
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(normalized)) {
		if (value !== undefined && value !== '') params.set(key, String(value));
	}
	return request<AdminUserPage>(`/api/admin/users?${params}`);
}

export async function fetchUserDetail(userId: string): Promise<AdminUserDetail> {
	return (await request<{ user: AdminUserDetail }>(`/api/admin/users/${userId}`)).user;
}

export async function fetchReports(): Promise<AdminReport[]> {
	return (await request<{ reports: AdminReport[] }>('/api/admin/reports')).reports;
}

export async function fetchAudit(): Promise<{
	entries: AdminAuditEntry[];
	integrity: boolean;
}> {
	return request('/api/admin/audit');
}

export async function exportAudit(format: 'csv' | 'json'): Promise<void> {
	const response = await authorizedFetch(`/api/admin/audit/export?format=${format}`, {
		method: 'GET'
	});
	if (!response.ok) throw new Error('Не удалось экспортировать аудит');
	const url = URL.createObjectURL(await response.blob());
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = `andresot-audit.${format}`;
	anchor.click();
	setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export async function setBlocked(
	userId: string,
	blocked: boolean,
	reason?: string,
	mfaCode?: string
): Promise<AdminUser> {
	return (
		await request<{ user: AdminUser }>(`/api/admin/users/${userId}/block`, {
			method: 'PATCH',
			body: { blocked, reason },
			headers: mfaCode ? { 'x-admin-mfa-code': mfaCode } : undefined
		})
	).user;
}

export async function setRole(
	userId: string,
	role: AdminRoleInput['role'],
	mfaCode?: string,
	reason?: string
): Promise<AdminUser> {
	return (
		await request<{ user: AdminUser }>(`/api/admin/users/${userId}/role`, {
			method: 'PATCH',
			body: { role, reason },
			headers: mfaCode ? { 'x-admin-mfa-code': mfaCode } : undefined
		})
	).user;
}

export async function setOfficial(userId: string, official: boolean): Promise<boolean> {
	return (
		await request<{ official: boolean }>(`/api/admin/users/${userId}/profile`, {
			method: 'PATCH',
			body: { official }
		})
	).official;
}

export async function updateUserProfile(
	userId: string,
	input: AdminUserProfileInput
): Promise<AdminUserDetail> {
	return (
		await request<{ user: AdminUserDetail }>(`/api/admin/users/${userId}/profile`, {
			method: 'PATCH',
			body: input
		})
	).user;
}

export async function resendUserVerification(userId: string): Promise<void> {
	await request(`/api/admin/users/${userId}/verification/resend`, { method: 'POST' });
}

export async function retryUserMailbox(userId: string): Promise<void> {
	await request(`/api/admin/users/${userId}/mailbox/retry`, { method: 'POST' });
}

export async function fetchUserBenefits(
	userId: string,
	cursor?: string,
	limit = 30
): Promise<{ benefits: AdminBenefits; operations: AdminBenefitOperation[]; nextCursor?: string }> {
	const params = new URLSearchParams({ limit: String(limit) });
	if (cursor) params.set('cursor', cursor);
	return request(`/api/admin/users/${userId}/benefits?${params}`);
}

export async function adjustStars(
	userId: string,
	input: AdminStarsAdjustInput,
	mfaCode?: string
): Promise<AdminBenefitResult> {
	return request(`/api/admin/users/${userId}/stars`, {
		method: 'POST',
		body: input,
		headers: mfaCode ? { 'x-admin-mfa-code': mfaCode } : undefined
	});
}

/** Начисление сразу нескольким: один код 2FA на весь список. */
export async function grantStarsBulk(
	input: { userIds: string[]; amount: number; reason: string; clientId: string },
	mfaCode?: string
): Promise<{ results: AdminStarsBulkOutcome[]; granted: number }> {
	return request('/api/admin/benefits/stars/bulk', {
		method: 'POST',
		body: input,
		headers: mfaCode ? { 'x-admin-mfa-code': mfaCode } : undefined
	});
}

export async function adjustPremium(
	userId: string,
	input: Extract<AdminPremiumAdjustInput, { action: string }>,
	mfaCode?: string
): Promise<AdminBenefitResult> {
	return request(`/api/admin/users/${userId}/premium`, {
		method: 'POST',
		body: input,
		headers: mfaCode ? { 'x-admin-mfa-code': mfaCode } : undefined
	});
}

export async function fetchBenefitOperations(
	query: Partial<AdminBenefitHistoryQuery> = {}
): Promise<{ operations: AdminBenefitOperation[]; nextCursor?: string }> {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(query)) {
		if (value !== undefined) params.set(key, String(value));
	}
	return request(`/api/admin/benefits?${params}`);
}

export async function exportBenefits(query: Partial<AdminBenefitHistoryQuery> = {}): Promise<void> {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(query)) {
		if (value !== undefined && key !== 'cursor' && key !== 'limit') params.set(key, String(value));
	}
	const response = await authorizedFetch(`/api/admin/benefits/export?${params}`, { method: 'GET' });
	if (!response.ok) throw new Error('Не удалось экспортировать операции');
	const url = URL.createObjectURL(await response.blob());
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = 'andresot-benefits.csv';
	anchor.click();
	setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export async function revokeSessions(
	userId: string,
	reason: string,
	mfaCode?: string
): Promise<number> {
	return (
		await request<{ revoked: number }>(`/api/admin/users/${userId}/revoke-sessions`, {
			method: 'POST',
			body: { reason },
			headers: mfaCode ? { 'x-admin-mfa-code': mfaCode } : undefined
		})
	).revoked;
}

export async function updateReport(
	reportId: string,
	status: AdminReportUpdate['status']
): Promise<AdminReport> {
	return (
		await request<{ report: AdminReport }>(`/api/admin/reports/${reportId}`, {
			method: 'PATCH',
			body: { status }
		})
	).report;
}

export async function createReport(input: ReportCreateInput): Promise<AdminReport> {
	return (await request<{ report: AdminReport }>('/api/reports', { method: 'POST', body: input }))
		.report;
}

export async function updateSupportTicket(
	ticketId: string,
	input: AdminSupportTicketUpdate
): Promise<AdminSupportTicket> {
	return (
		await request<{ ticket: AdminSupportTicket }>(`/api/admin/support/${ticketId}`, {
			method: 'PATCH',
			body: input
		})
	).ticket;
}

export async function createBroadcast(input: AdminBroadcastCreate): Promise<AdminBroadcast> {
	return (
		await request<{ broadcast: AdminBroadcast }>('/api/admin/broadcasts', {
			method: 'POST',
			body: input
		})
	).broadcast;
}

export async function updateBroadcast(
	broadcastId: string,
	input: AdminBroadcastCreate
): Promise<AdminBroadcast> {
	return (
		await request<{ broadcast: AdminBroadcast }>(`/api/admin/broadcasts/${broadcastId}`, {
			method: 'PATCH',
			body: input
		})
	).broadcast;
}

export async function fetchBroadcast(broadcastId: string): Promise<AdminBroadcast> {
	return (await request<{ broadcast: AdminBroadcast }>(`/api/admin/broadcasts/${broadcastId}`))
		.broadcast;
}

/** Сколько человек получит рассылку с такой аудиторией. */
export async function countBroadcastAudience(audience: AdminBroadcastAudience): Promise<number> {
	return (
		await request<{ count: number }>(
			`/api/admin/broadcasts/audience?audience=${encodeURIComponent(audience)}`
		)
	).count;
}

/** «Отправить себе»: рассылка приходит в свой чат «ANDRESOT», без статистики. */
export async function testBroadcast(
	broadcastId: string
): Promise<{ chatId?: string; push: string | null }> {
	return request(`/api/admin/broadcasts/${broadcastId}/test`, { method: 'POST' });
}

export async function actOnBroadcast(
	broadcastId: string,
	action: 'send' | 'cancel',
	mfaCode?: string
): Promise<AdminBroadcast> {
	return (
		await request<{ broadcast: AdminBroadcast }>(`/api/admin/broadcasts/${broadcastId}/action`, {
			method: 'POST',
			body: { action },
			headers: mfaCode ? { 'x-admin-mfa-code': mfaCode } : undefined
		})
	).broadcast;
}

export async function updatePlatformSettings(
	input: AdminPlatformSettingsInput,
	mfaCode?: string
): Promise<AdminPlatformSettings> {
	return (
		await request<{ settings: AdminPlatformSettings }>('/api/admin/platform-settings', {
			method: 'PATCH',
			body: input,
			headers: mfaCode ? { 'x-admin-mfa-code': mfaCode } : undefined
		})
	).settings;
}

export async function updatePaymentSettings(
	input: AdminPaymentSettingsInput,
	mfaCode?: string
): Promise<AdminPaymentSettings> {
	return (
		await request<{ paymentSettings: AdminPaymentSettings }>('/api/admin/payment-settings', {
			method: 'PATCH',
			body: input,
			headers: mfaCode ? { 'x-admin-mfa-code': mfaCode } : undefined
		})
	).paymentSettings;
}

/** Проверка связи с кассой: показываем дословный ответ, не толкуя его. */
export async function checkPaymentSettings(): Promise<{ ok: boolean; message: string }> {
	return request('/api/admin/payment-settings/check', { method: 'POST', body: {} });
}

export async function beginMfaSetup(): Promise<AdminMfaSetup> {
	return (
		await request<{ setup: AdminMfaSetup }>('/api/admin/mfa/setup', { method: 'POST', body: {} })
	).setup;
}

export async function confirmMfa(code: string): Promise<void> {
	await request('/api/admin/mfa/confirm', { method: 'POST', body: { code } });
}

export async function disableMfa(code: string): Promise<void> {
	await request('/api/admin/mfa', { method: 'DELETE', body: { code } });
}

export async function hidePublicContent(
	kind: 'channel_post' | 'channel_comment',
	id: string,
	reason: string
): Promise<void> {
	await request('/api/admin/public-content/hide', {
		method: 'POST',
		body: { kind, id, reason }
	});
}
